import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Upload } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

/**
 * Homogeneous Template / Import / Export toolbar used across the 3
 * tournament types (Bracket, Circle Qualification, 1v1 Battles).
 *
 * Props:
 *  - templateUrl : direct GET URL that returns the CSV template
 *  - importUrl   : POST endpoint that accepts a multipart file
 *  - exportUrl   : direct GET URL that returns the results CSV
 *  - testIdPrefix: string prefix for data-testid (defaults to "tournament-io")
 *  - importParams: optional query string appended to importUrl
 *                  (e.g. "replace=false" for battles-list)
 *  - onImported  : (response.data) => void  called on successful import
 *
 * Accepts both .csv and .xlsx for import.
 */
export default function TournamentIOToolbar({
  templateUrl,
  importUrl,
  exportUrl,
  testIdPrefix = "tournament-io",
  importParams = "",
  onImported,
}) {
  const { t } = useI18n();

  const downloadTemplate = () => {
    if (!templateUrl) return;
    window.open(templateUrl, "_blank");
  };

  const exportResults = () => {
    if (!exportUrl) return;
    window.open(exportUrl, "_blank");
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-upload of the same file
    if (!file || !importUrl) return;

    const fd = new FormData();
    fd.append("file", file);
    try {
      const url = importParams ? `${importUrl}?${importParams}` : importUrl;
      const r = await axios.post(url, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = r.data || {};
      // Homogeneous success signal: any of {created, updated, success_count}
      const successCount =
        data.created ?? data.updated ?? data.success_count ?? 0;
      const errs = data.errors || [];
      if (successCount > 0) {
        toast.success(t("tournamentIO.imported", { count: successCount }));
      }
      if (errs.length > 0) {
        toast.error(t("tournamentIO.importErrors", { count: errs.length }));
        // eslint-disable-next-line no-console
        console.warn("Import errors:", errs);
      }
      if (onImported) onImported(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div className="flex gap-2 flex-wrap" data-testid={`${testIdPrefix}-toolbar`}>
      {templateUrl && (
        <Button
          variant="outline"
          onClick={downloadTemplate}
          data-testid={`${testIdPrefix}-template-btn`}
        >
          <Download size={16} className="mr-2" />
          {t("tournamentIO.template")}
        </Button>
      )}
      {importUrl && (
        <label className="inline-flex items-center">
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={handleImport}
            className="hidden"
            data-testid={`${testIdPrefix}-import-input`}
          />
          <span
            className="inline-flex items-center h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm cursor-pointer hover:opacity-90"
            data-testid={`${testIdPrefix}-import-btn`}
          >
            <Upload size={16} className="mr-2" />
            {t("tournamentIO.import")}
          </span>
        </label>
      )}
      {exportUrl && (
        <Button
          variant="outline"
          onClick={exportResults}
          data-testid={`${testIdPrefix}-export-btn`}
        >
          <Download size={16} className="mr-2" />
          {t("tournamentIO.export")}
        </Button>
      )}
    </div>
  );
}
